import { setGridVisibility, setSpaceVisibility, ToggleInvisibleAsXray } from "@/Context/ViewerContext/Reducer";
import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import clsx from "clsx";
import { Settings } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import packageJson from "../../../package.json";
import { useTheme } from "../theme-provider";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { Toggle } from "../ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const Shortcut = ({ children, text, className }: { children: React.ReactNode; text: string; className?: string }) => {
    return (
        <div className={clsx(["flex items-center space-x-2", className])}>
            <div className="flex items-center space-x-2 bg-zinc-200 border-zinc-300	dark:bg-zinc-700 dark:border-zinc-800 rounded-md border-2 mr-2 font-mono min-w-10 h-10 justify-center">
                {children}
            </div>
            {text}
        </div>
    );
};

export type Settings = {
    gridEnabled: boolean;
    spaceEnabled: boolean;
    invisibleAsXray: boolean;
    language: string;
};

const Configurations = () => {
    const { dispatch } = useContext(ViewerContext);
    const { i18n, t } = useTranslation();
    const { theme, setTheme } = useTheme();

    const [open, setOpen] = useState<boolean>(false);
    const [gridEnabled, setGridEnabled] = useState<boolean>(false);
    const [spaceEnabled, setSpaceEnabled] = useState<boolean>(false);
    const [invisibleAsXray, setInvisibleAsXray] = useState<boolean>(false);

    const loadSettings = () => {
        const settingsString = localStorage.getItem("viewerSettings");
        if (settingsString) {
            const settings: Settings = JSON.parse(settingsString);
            setGridEnabled(settings.gridEnabled);
            setSpaceEnabled(settings.spaceEnabled);
            setInvisibleAsXray(settings.invisibleAsXray);
        }
    };

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    const saveSettings = (e: React.FormEvent) => {
        e.preventDefault();

        const settings: Settings = {
            gridEnabled,
            spaceEnabled,
            invisibleAsXray,
            language: i18n.language,
        };

        localStorage.setItem("viewerSettings", JSON.stringify(settings));

        dispatch(setGridVisibility(settings.gridEnabled));
        dispatch(setSpaceVisibility(settings.spaceEnabled));
        dispatch(ToggleInvisibleAsXray(settings.invisibleAsXray));
        changeLanguage(settings.language);

        setOpen(false);
    };

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        loadSettings();
    }, []);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <form onSubmit={saveSettings}>
                <DialogTrigger asChild>
                    <div>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex">
                                    <Toggle pressed={false}>
                                        <Settings className="h-4 w-4" />
                                    </Toggle>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("settings")}</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </DialogTrigger>
                <DialogContent className="max-w-full sm:max-w-4xl max-h-full h-full sm:h-fit select-none overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t("settings")}</DialogTitle>
                        <DialogDescription>{t("settingsDescription")}</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.5fr)] gap-6">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="grid-visibility"
                                    checked={gridEnabled}
                                    onCheckedChange={(checked) => setGridEnabled(!!checked)}
                                />
                                <div className="grid gap-1">
                                    <Label htmlFor="grid-visibility">{t("grid")}</Label>
                                    <p className="text-muted-foreground text-sm">{t("gridDescription")}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="space-visibility"
                                    checked={spaceEnabled}
                                    onCheckedChange={(checked) => setSpaceEnabled(!!checked)}
                                />
                                <div className="grid gap-1">
                                    <Label htmlFor="space-visibility">{t("space")}</Label>
                                    <p className="text-muted-foreground text-sm">{t("spaceDescription")}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="xray-invisibility"
                                    checked={invisibleAsXray}
                                    onCheckedChange={(checked) => setInvisibleAsXray(!!checked)}
                                />
                                <div className="grid gap-1">
                                    <Label htmlFor="xray-invisibility">{t("invisibleAsXray")}</Label>
                                    <p className="text-muted-foreground text-sm">{t("invisibleAsXrayDescription")}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col mb-2 gap-3">
                            <Select onValueChange={changeLanguage} value={i18n.language ?? "en"}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>{t("language")}</SelectLabel>
                                        <SelectItem value="en">English</SelectItem>
                                        <SelectItem value="nl">Nederlands</SelectItem>
                                        <SelectItem value="fr">Français</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <Select onValueChange={setTheme} value={theme}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select theme" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>Theme</SelectLabel>
                                        <SelectItem value="light">Light</SelectItem>
                                        <SelectItem value="dark">Dark</SelectItem>
                                        <SelectItem value="system">System</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Separator />
                    <h2 className="text-base font-semibold">{t("shortcut")}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Shortcut text={t("navigateForward")}>W</Shortcut>
                        <Shortcut text={t("navigateBackward")}>S</Shortcut>
                        <Shortcut text={t("navigateDown")}>Z</Shortcut>
                        <Shortcut text={t("navigateUp")}>X</Shortcut>
                        <Shortcut text={t("navigateLeft")}>A</Shortcut>
                        <Shortcut text={t("navigateRight")}>D</Shortcut>
                        <Shortcut text={t("rotateClockwise")}>Q</Shortcut>
                        <Shortcut text={t("rotateCounterclockwise")}>E</Shortcut>
                        <Shortcut text={t("selectObject")}>
                            <img src="./leftclick.svg" className="h-5 float-right dark:invert" />
                        </Shortcut>
                        <Shortcut text={t("openMenu")}>
                            <img src="./rightclick.svg" className="h-5 float-right dark:invert" />
                        </Shortcut>
                        <Shortcut text={t("selectMultiple")} className="col-span-2">
                            <div className="flex px-3 items-center justify-center w-full">
                                CTRL+
                                <img src="./leftclick.svg" className="h-5 pl-1 float-right dark:invert" />
                            </div>
                        </Shortcut>
                    </div>
                    <Separator />
                    <DialogFooter >
                        <DialogClose asChild>
                            <Button variant="outline" className="mt-2 sm:mt-0">Cancel</Button>
                        </DialogClose>
                        <Button type="submit" onClick={saveSettings}>
                            Save changes
                        </Button>
                    </DialogFooter>
                    <div className="text-center text-gray-400 text-sm">Version {packageJson.version}</div>
                </DialogContent>
            </form>
        </Dialog>
    );
};

export default Configurations;
